/**
 * Per-position colour palette for branch swatches and ProgressBar fills.
 * The first four positions get distinct accent hues; the rest fall back
 * to the neutral default so the colour vocabulary doesn't blow up when
 * there are many branches. Values are CSS variables — fed straight to
 * inline `background-color` on the swatch and the ProgressBar fill, so
 * both always agree on hue per branch.
 */
const BRANCH_COLORS: readonly string[] = [
    'var(--success)',
    'var(--accent)',
    'var(--warning)',
    'var(--danger)',
    'var(--default-500)',
];

/**
 * Maps a 0-based row index to its branch colour, cycling through the
 * palette if the branch list exceeds its length.
 */
export const getBranchColor = (index: number): string => {
    const len = BRANCH_COLORS.length;
    const wrapped = ((index % len) + len) % len;
    return BRANCH_COLORS[wrapped]!;
};

/**
 * Clamps the API's `percentageOfTop` to `[0, 100]` for the ProgressBar
 * value, defensively guarding against future server quirks.
 */
/**
 * What the widget's subtitle says.
 *
 * A BranchAdmin sees only their own branch (bos-api BOS-133), so the card
 * shows one row at a permanent 100% — "sales by branch" over a list of one,
 * comparing it to itself. It says "Your branch" instead, and the share bar is
 * dropped (see {@link showsShareOfTop}).
 */
export const subtitleKeyFor = (branchCount: number): string =>
    branchCount === 1
        ? 'sales.widgets.branchComparison.yourBranch'
        : 'sales.widgets.branchComparison.subtitle';

/**
 * Whether the "% of the top branch" bar means anything. With one branch it is
 * always 100% of itself, which is a measurement of nothing.
 */
export const showsShareOfTop = (branchCount: number): boolean =>
    branchCount > 1;

export const clampPercent = (value: number): number => {
    return Math.max(0, Math.min(100, value));
};
